import { useState } from "react";
import Modal from "./Modal";
import Button from "./Inputs/Button";
import Select from "./Inputs/Select";
import Input from "./Inputs/Input";
import { createTask } from "src/services/client";
import { Client } from "src/types";
import { useUser } from "src/hooks/useUser";

interface Task {
  id: string;
  description: string;
  assignee?: string;
  dueDate?: string;
  completed: boolean;
  createdAt: string;
  updatedAt: string;
  clientId: number;
  client: {
    id: number;
    title: string;
  };
}

interface AddTaskProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated: (task: Task) => void;
  clients: Client[];
  defaultClientId?: number;
}

interface AddTaskFormProps {
  onTaskCreated: (task: Task) => void;
  hideCancel?: boolean;
  onCancel: () => void;
  clients: Client[];
  defaultClientId?: number;
}

const AddTask = ({
  isOpen,
  onClose,
  onTaskCreated,
  clients,
  defaultClientId,
}: AddTaskProps) => {
  const { user } = useUser();

  const [newTask, setNewTask] = useState({
    description: "",
    assignee: "advisor" as const,
    dueDate: "",
    clientId: defaultClientId || 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setNewTask({
      description: "",
      assignee: "advisor",
      dueDate: "",
      clientId: defaultClientId || 0,
    });
    onClose();
  };

  const addTask = async () => {
    if (!newTask.description.trim() || !newTask.clientId || isSubmitting)
      return;

    const selectedClient = clients.find((c) => c.id === newTask.clientId);
    if (!selectedClient) return;

    setIsSubmitting(true);

    try {
      const response = await createTask({
        description: newTask.description,
        assignee: newTask.assignee,
        dueDate: newTask.dueDate || undefined,
        clientId: newTask.clientId,
      });

      if (response.ok) {
        const createdTask = await response.json();
        onTaskCreated(createdTask);
        handleClose();
      } else {
        console.error("Failed to create task");
      }
    } catch (error) {
      console.error("Error creating task:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="w-full max-w-md mx-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Add New Task</h2>

        <div className="space-y-4">
          <Input
            value={newTask.description}
            label="Description"
            width="w-full h-[100px]"
            vertical
            subtype="textarea"
            setValue={(v) =>
              setNewTask({
                ...newTask,
                description: v,
              })
            }
            placeholder="Enter task description..."
          />

          <div>
            <Select
              vertical
              label="Assignee"
              selected={{
                name:
                  newTask.assignee === "advisor"
                    ? user?.info?.name || "Advisor"
                    : "Client",
                value: newTask.assignee,
              }}
              setSelected={(option) =>
                setNewTask({
                  ...newTask,
                  assignee: option.value as any,
                })
              }
              options={[
                { name: user?.info?.name || "Advisor", value: "advisor" },
                { name: "Client", value: "client" },
              ]}
            />
          </div>

          <Input
            value={newTask.dueDate}
            vertical
            label="Due Date (Optional)"
            subtype="date"
            width="!w-[200px]"
            setValue={(v) =>
              setNewTask({
                ...newTask,
                dueDate: v,
              })
            }
          />
        </div>

        <div className="flex gap-3 mt-6 justify-end">
          <Button
            type="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={addTask}
            disabled={
              !newTask.description.trim() || !newTask.clientId || isSubmitting
            }
          >
            {isSubmitting ? "Creating..." : "Create Task"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const AddTaskForm = ({
  onTaskCreated,
  onCancel,
  clients,
  defaultClientId,
}: AddTaskFormProps) => {
  const { user } = useUser();

  const [newTask, setNewTask] = useState({
    description: "",
    assignee: "advisor" as const,
    dueDate: "",
    clientId: defaultClientId || 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = () => {
    setNewTask({
      description: "",
      assignee: "advisor",
      dueDate: "",
      clientId: defaultClientId || 0,
    });
    onCancel();
  };

  const addTask = async () => {
    if (!newTask.description.trim() || !newTask.clientId || isSubmitting)
      return;

    const selectedClient = clients.find((c) => c.id === newTask.clientId);
    if (!selectedClient) return;

    setIsSubmitting(true);

    try {
      const response = await createTask({
        description: newTask.description,
        assignee: newTask.assignee,
        dueDate: newTask.dueDate || undefined,
        clientId: newTask.clientId,
      });

      if (response.ok) {
        const createdTask = await response.json();
        onTaskCreated(createdTask);
        setNewTask({
          description: "",
          assignee: "advisor",
          dueDate: "",
          clientId: defaultClientId || 0,
        });
      } else {
        console.error("Failed to create task");
      }
    } catch (error) {
      console.error("Error creating task:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="space-y-4">
        <Input
          value={newTask.description}
          label="Description"
          width="w-full h-[100px]"
          vertical
          subtype="textarea"
          setValue={(v) =>
            setNewTask({
              ...newTask,
              description: v,
            })
          }
          placeholder="Enter task description..."
        />

        <div>
          <Select
            vertical
            label="Assignee"
            selected={{
              name:
                newTask.assignee === "advisor"
                  ? user?.info?.name || "Advisor"
                  : "Client",
              value: newTask.assignee,
            }}
            setSelected={(option) =>
              setNewTask({
                ...newTask,
                assignee: option.value as any,
              })
            }
            options={[
              { name: user?.info?.name || "Advisor", value: "advisor" },
              { name: "Client", value: "client" },
            ]}
          />
        </div>

        <Input
          value={newTask.dueDate}
          vertical
          label="Due Date (Optional)"
          subtype="date"
          width="!w-full"
          setValue={(v) =>
            setNewTask({
              ...newTask,
              dueDate: v,
            })
          }
        />
      </div>

      <div className="flex gap-2 mt-6 justify-end">
        <Button type="secondary" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>

        <Button
          type="primary"
          onClick={addTask}
          disabled={
            !newTask.description.trim() || !newTask.clientId || isSubmitting
          }
        >
          {isSubmitting ? "Creating..." : "Create"}
        </Button>
      </div>
    </div>
  );
};

export default AddTask;
